import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { SERVICES } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  phone: "",
  email: "",
  vehicle_make: "",
  vehicle_model: "",
  vehicle_year: "",
  service: SERVICES[0].title,
  message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/devis`, form);
      setSent(true);
      toast.success("Devis envoyé !", {
        description: "Nous vous recontactons dans les meilleurs délais.",
      });
      setForm(initial);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Une erreur est survenue. Réessayez ou appelez-nous.";
      toast.error("Échec de l'envoi", { description: String(msg) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#050505] py-24 md:py-32 px-6 md:px-12 overflow-hidden"
    >
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-yellow-500/5 blur-3xl pointer-events-none" />
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left side */}
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-yellow-500" />
            <span className="text-yellow-500 text-xs uppercase tracking-[0.4em]">
              04 — Devis gratuit
            </span>
          </div>
          <h2 className="font-display text-white text-5xl md:text-6xl leading-[0.95] mb-6">
            Parlez-nous de<br />
            <span className="text-yellow-500">votre projet.</span>
          </h2>
          <p className="text-zinc-400 text-base md:text-lg leading-relaxed mb-10 max-w-md">
            Décrivez votre véhicule et ce que vous souhaitez. On vous répond
            personnellement avec une estimation claire et sans engagement.
          </p>

          <div className="space-y-4">
            <div className="border-l-2 border-yellow-500 pl-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                Téléphone direct
              </div>
              <a
                href="tel:+33675368290"
                className="font-display text-3xl text-white hover:text-yellow-500 transition-colors"
                data-testid="contact-phone"
              >
                06 75 36 82 90
              </a>
            </div>
            <div className="border-l-2 border-yellow-500 pl-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                Email
              </div>
              <a
                href="mailto:ladperformance66@gmail.com"
                className="font-display text-xl text-white hover:text-yellow-500 transition-colors break-all"
                data-testid="contact-email"
              >
                ladperformance66@gmail.com
              </a>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="devis-form"
            className="bg-[#0d0d0d] border border-white/10 p-6 md:p-10 space-y-5"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field
                label="Nom complet *"
                id="name"
                value={form.name}
                onChange={update("name")}
                placeholder="Jean Dupont"
                required
              />
              <Field
                label="Téléphone *"
                id="phone"
                type="tel"
                value={form.phone}
                onChange={update("phone")}
                placeholder="06 12 34 56 78"
                required
              />
            </div>

            <Field
              label="Email *"
              id="email"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="vous@exemple.com"
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <Field
                label="Marque *"
                id="vehicle_make"
                value={form.vehicle_make}
                onChange={update("vehicle_make")}
                placeholder="Peugeot"
                required
              />
              <Field
                label="Modèle *"
                id="vehicle_model"
                value={form.vehicle_model}
                onChange={update("vehicle_model")}
                placeholder="308 GTi"
                required
              />
              <Field
                label="Année"
                id="vehicle_year"
                value={form.vehicle_year}
                onChange={update("vehicle_year")}
                placeholder="2018"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="service"
                className="block text-[10px] uppercase tracking-[0.3em] text-zinc-400"
              >
                Prestation souhaitée *
              </label>
              <select
                id="service"
                data-testid="form-service"
                value={form.service}
                onChange={update("service")}
                required
                className="w-full bg-black border border-white/15 text-white px-4 py-3 focus:outline-none focus:border-yellow-500 transition-colors"
              >
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title}>
                    {s.title}
                  </option>
                ))}
                <option value="Autre">Autre / je ne sais pas</option>
              </select>
            </div>

            <div className="space-y-2">
              <label
                htmlFor="message"
                className="block text-[10px] uppercase tracking-[0.3em] text-zinc-400"
              >
                Votre message *
              </label>
              <textarea
                id="message"
                data-testid="form-message"
                value={form.message}
                onChange={update("message")}
                rows={5}
                required
                placeholder="Décrivez votre véhicule, vos modifications actuelles, votre objectif (gain de puissance, cartographie E85, etc.)"
                className="w-full bg-black border border-white/15 text-white px-4 py-3 focus:outline-none focus:border-yellow-500 transition-colors resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={loading || sent}
              data-testid="form-submit"
              className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-yellow-500 hover:bg-yellow-400 disabled:bg-yellow-500/40 text-black px-10 py-4 font-medium text-sm uppercase tracking-[0.2em] transition-colors"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : sent ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {sent ? "Envoyé" : loading ? "Envoi…" : "Envoyer ma demande"}
            </button>
            <p className="text-[11px] text-zinc-500 tracking-wide">
              Vos données restent confidentielles. Aucun spam, juste une réponse personnelle.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, id, type = "text", value, onChange, placeholder, required }) => (
  <div className="space-y-2">
    <label
      htmlFor={id}
      className="block text-[10px] uppercase tracking-[0.3em] text-zinc-400"
    >
      {label}
    </label>
    <input
      id={id}
      data-testid={`form-${id}`}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      className="w-full bg-black border border-white/15 text-white px-4 py-3 placeholder:text-zinc-600 focus:outline-none focus:border-yellow-500 transition-colors"
    />
  </div>
);
